'use strict';

const libPictProvider = require('pict-provider');
const libUserSearch = require('../access/User-Search.js');

// The New-Customer document type (ReportNamedInstance Hash) — stable across environments, so the New Customers tab
// filters on it rather than on an environment-specific project id. Provisioning requests are Documents of this type
// (back-filled existing customers carry a GUIDDocument of `NEWCUST-BF-<IDCustomer>`). See [[config-app-customer-dashboard]].
const NEW_CUSTOMER_DOCTYPE = 'HeadLight-NewCustomer';
const BACKFILL_GUID_PREFIX = 'NEWCUST-BF-';

/**
 * CustomersList-Provider — data + logic for the #/Customers screen. Two tabs share this provider + the Customers view:
 *  - "Customers" (the default): the customer roster (front door to the per-customer Customer workspace). Each row
 *    opens the workspace (#/Customer/Workspace/:IDCustomer) and — for a session that may impersonate — carries a
 *    "Manage Configuration" button that signs in as the customer's configuration admin.
 *  - "New Customers" (#/Customers/New): the provisioning-request Documents (type HeadLight-NewCustomer), each with a
 *    status badge; "+ New Customer" opens the provisioning wizard. Existing customers appear here as Provisioned.
 *
 * Replaces the old #/ManageCustomers list; the Modules + Customer Team tooling that used to sit beside it now lives
 * in the workspace (source/providers/CustomerWorkspace-Provider.js). See [[config-app-customer-dashboard]].
 */
class CustomersListProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
		this.state = { ViewMode: 'roster', Loaded: false, Loading: false, Error: '', Customers: [], Filter: '',
			// A customer row expands to list its configuration-admin accounts so you can sign in as a SPECIFIC one
			// (the roster + "Sign in as" picker the old #/ManageCustomers screen had — not a one-click auto-pick).
			ExpandedCustomerID: 0, AccountsByCustomer: {}, AccountsLoadingID: 0, AccountsError: '',
			// "New Customers" tab — the provisioning-request Documents.
			NewLoaded: false, NewLoading: false, NewError: '', NewDocs: [], NewFilter: '', NewStatusFilter: 'all', NewBusyID: 0 };
	}

	get restClient() { return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient; }
	get app() { return this.pict.PictApplication; }
	get modal() { return this.pict.views['Pict-Section-Modal']; }

	_esc(pStr) { return String(pStr == null ? '' : pStr).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar])); }
	_getJSON(pURL) { return new Promise((resolve, reject) => this.restClient.getJSON(pURL, (pError, pBody) => pError ? reject(pError) : resolve(pBody))); }
	_postJSON(pURL, pBody) { return new Promise((resolve, reject) => this.restClient.postJSON(pURL, pBody, (pError, pResult) => pError ? reject(pError) : resolve(pResult))); }
	_toast(pMessage, pType) { if (this.modal && (typeof this.modal.toast === 'function')) { this.modal.toast(pMessage, { type: pType || 'success' }); } }

	/** UI gate for the Manage Configuration affordance (impersonation). The server stays authoritative. */
	canImpersonate() { return !!(this.app && (typeof this.app.canImpersonate === 'function') && this.app.canImpersonate()); }

	async loadAsync()
	{
		this.state.Loading = true;
		this.state.Error = '';
		this.render();
		try
		{
			// The server scopes the list to what this session may see (no client-side customer filter).
			const tmpRows = await this._getJSON('Customers/0/2000');
			this.state.Customers = (Array.isArray(tmpRows) ? tmpRows : [])
				.map((pCustomer) => ({ IDCustomer: pCustomer.IDCustomer, Name: pCustomer.Name || `Customer #${pCustomer.IDCustomer}`, Code: pCustomer.Code || '', Enabled: pCustomer.Enabled }))
				.sort((pA, pB) => String(pA.Name).localeCompare(String(pB.Name)));
			this.state.Loaded = true;
		}
		catch (pError)
		{
			this.state.Error = `Could not load customers: ${(pError && pError.message) ? pError.message : pError}`;
		}
		this.state.Loading = false;
		this.render();
	}

	render()
	{
		const tmpCA = this.pict.ContentAssignment;
		if (tmpCA.getElement('#CL-Body').length === 0) { return; }
		tmpCA.assignContent('#CL-Body', this._bodyHTML());
	}

	/** Switch between the "Customers" roster and the "New Customers" request list (driven by the router per route). */
	setMode(pMode)
	{
		this.state.ViewMode = (pMode === 'new') ? 'new' : 'roster';
		if ((this.state.ViewMode === 'new') && !this.state.NewLoaded && !this.state.NewLoading) { this.loadNewCustomersAsync(); return; }
		this.render();
	}

	_bodyHTML()
	{
		return this._tabStripHTML() + ((this.state.ViewMode === 'new') ? this._newBodyHTML() : this._rosterBodyHTML());
	}

	_tabStripHTML()
	{
		const tmpNew = (this.state.ViewMode === 'new');
		return `<div class="cl-tabs">`
			+ `<a class="cl-tab${tmpNew ? '' : ' is-active'}" href="#/Customers"><i class="fas fa-city"></i> Customers</a>`
			+ `<a class="cl-tab${tmpNew ? ' is-active' : ''}" href="#/Customers/New"><i class="fas fa-wand-magic-sparkles"></i> New Customers</a>`
			+ `</div>`;
	}

	_rosterBodyHTML()
	{
		if (this.state.Loading && !this.state.Loaded) { return '<div class="cl-note"><i class="fas fa-spinner fa-spin"></i> Loading customers…</div>'; }
		if (this.state.Error) { return `<div class="cl-error"><i class="fas fa-triangle-exclamation"></i> ${this._esc(this.state.Error)}</div>`; }
		return `<input id="CL-Filter" class="cl-filter" type="text" autocomplete="off" placeholder="Filter customers by name or code…" value="${this._esc(this.state.Filter)}" oninput="_Pict.providers.CustomersList.onFilter(this.value)" />`
			+ `<div id="CL-List" class="cl-list">${this._listHTML()}</div>`;
	}

	/** Filter in place — only the list re-renders, so the filter input keeps focus. */
	onFilter(pValue) { this.state.Filter = pValue; this._renderList(); }

	_renderList()
	{
		const tmpCA = this.pict.ContentAssignment;
		const tmpEl = tmpCA.getElement('#CL-List');
		if (!tmpEl || !tmpEl.length) { return; }
		const tmpTop = tmpEl[0].scrollTop;
		tmpCA.assignContent('#CL-List', this._listHTML());
		const tmpEl2 = tmpCA.getElement('#CL-List');
		if (tmpEl2 && tmpEl2.length) { tmpEl2[0].scrollTop = tmpTop; }
	}

	_listHTML()
	{
		const tmpTerm = String(this.state.Filter || '').trim().toLowerCase();
		const tmpRows = this.state.Customers.filter((pCustomer) =>
			!tmpTerm || (String(pCustomer.Name).toLowerCase().indexOf(tmpTerm) > -1) || (String(pCustomer.Code).toLowerCase().indexOf(tmpTerm) > -1));
		if (!tmpRows.length) { return `<div class="cl-note">${tmpTerm ? 'No customers match your filter.' : 'No customers available.'}</div>`; }
		const tmpCanImpersonate = this.canImpersonate();
		const tmpHeader = '<div class="cl-head-row"><span class="cl-col-name">Customer</span><span class="cl-col-code">Code</span><span class="cl-col-action"></span></div>';
		return tmpHeader + tmpRows.map((pCustomer) =>
		{
			const tmpID = Number(pCustomer.IDCustomer);
			const tmpExpanded = (Number(this.state.ExpandedCustomerID) === tmpID);
			const tmpManage = tmpCanImpersonate
				? `<button type="button" class="cl-btn cl-btn-ghost${tmpExpanded ? ' is-open' : ''}" title="Sign in as one of this customer's configuration-admin accounts" onclick="event.stopPropagation(); _Pict.providers.CustomersList.toggleAccounts(${tmpID})"><i class="fas fa-user-gear"></i> Manage Configuration <i class="fas fa-chevron-${tmpExpanded ? 'up' : 'down'} cl-btn-caret"></i></button>`
				: '';
			return `<div class="cl-item${tmpExpanded ? ' is-expanded' : ''}">`
				+ `<div class="cl-row" role="button" tabindex="0" title="Open this customer's workspace" onclick="_Pict.providers.CustomersList.openWorkspace(${tmpID})" onkeydown="if(event.key==='Enter'||event.key===' '){event.preventDefault();_Pict.providers.CustomersList.openWorkspace(${tmpID});}">`
				+ `<span class="cl-col-name">${this._esc(pCustomer.Name)}</span>`
				+ `<span class="cl-col-code">${pCustomer.Code ? this._esc(pCustomer.Code) : `#${this._esc(pCustomer.IDCustomer)}`}</span>`
				+ `<span class="cl-col-action">${tmpManage}<i class="fas fa-chevron-right cl-open-caret"></i></span>`
				+ `</div>`
				+ (tmpExpanded ? `<div class="cl-accounts">${this._accountsHTML(tmpID)}</div>` : '')
				+ `</div>`;
		}).join('');
	}


	/** Open a customer's workspace (Overview / Users / Modules / Customer Team). */
	openWorkspace(pIDCustomer)
	{
		const tmpID = Number(pIDCustomer);
		if (!tmpID) { return; }
		if ((typeof window !== 'undefined') && window.location) { window.location.hash = `#/Customer/Workspace/${tmpID}`; }
	}

	/** Expand (or collapse) a customer row to show its configuration-admin accounts. First expand loads them. No modal. */
	toggleAccounts(pIDCustomer)
	{
		const tmpID = Number(pIDCustomer);
		if (Number(this.state.ExpandedCustomerID) === tmpID) { this.state.ExpandedCustomerID = 0; this._renderList(); return; }
		this.state.ExpandedCustomerID = tmpID;
		this.state.AccountsError = '';
		if (!this.state.AccountsByCustomer[tmpID]) { this._loadAccountsAsync(tmpID); }
		this._renderList();
	}

	/**
	 * Load a customer's impersonatable accounts — the users who hold a PermissionLevel-'CustomerAdmin' role in that
	 * customer (the config-app write gate). Mirrors the server's resolveCustomerAdminUser query, but lists ALL of them.
	 * Cross-customer reads ride the super-user session (same pattern as SuperUserGrant / Entitlements).
	 */
	async _loadAccountsAsync(pIDCustomer)
	{
		const tmpID = Number(pIDCustomer);
		this.state.AccountsLoadingID = tmpID;
		this.state.AccountsError = '';
		this._renderList();
		try
		{
			const tmpRoles = await this._getJSON(`Roles/FilteredTo/FBV~IDCustomer~EQ~${tmpID}~FBV~PermissionLevel~EQ~CustomerAdmin~FBV~Deleted~EQ~0/0/200`);
			const tmpRoleIDs = (Array.isArray(tmpRoles) ? tmpRoles : []).map((pRole) => pRole.IDRole).filter(Boolean);
			let tmpAccounts = [];
			if (tmpRoleIDs.length)
			{
				const tmpJoins = await this._getJSON(`UserRoleJoins/FilteredTo/FBL~IDRole~INN~${tmpRoleIDs.join(',')}/0/1000`);
				const tmpUserIDs = Array.from(new Set((Array.isArray(tmpJoins) ? tmpJoins : []).map((pJoin) => pJoin.IDUser).filter(Boolean)));
				if (tmpUserIDs.length)
				{
					// Only live, enabled users actually IN the target customer qualify (mirrors the server's gate).
					const tmpUsers = await this._getJSON(`Users/FilteredTo/FBL~IDUser~INN~${tmpUserIDs.join(',')}~FBV~IDCustomer~EQ~${tmpID}~FBV~Deleted~EQ~0~FBV~Disabled~EQ~0/0/1000`);
					tmpAccounts = (Array.isArray(tmpUsers) ? tmpUsers : [])
						.map((pUser) => ({ IDUser: pUser.IDUser, Name: libUserSearch.userLabel(pUser), LoginID: pUser.LoginID || '', Email: pUser.Email || '' }))
						.sort((pA, pB) => String(pA.Name).localeCompare(String(pB.Name)));
				}
			}
			if (Number(this.state.ExpandedCustomerID) === tmpID) { this.state.AccountsByCustomer[tmpID] = tmpAccounts; }
		}
		catch (pError)
		{
			if (Number(this.state.ExpandedCustomerID) === tmpID) { this.state.AccountsError = `Could not load accounts: ${(pError && pError.message) ? pError.message : pError}`; }
		}
		this.state.AccountsLoadingID = 0;
		this._renderList();
	}

	/** The impersonatable accounts for one customer: loading / error / empty / a list of "Sign in as" account buttons. */
	_accountsHTML(pIDCustomer)
	{
		const tmpID = Number(pIDCustomer);
		if (this.state.AccountsError) { return `<div class="cl-accounts-note cl-error"><i class="fas fa-triangle-exclamation"></i> ${this._esc(this.state.AccountsError)}</div>`; }
		const tmpAccounts = this.state.AccountsByCustomer[tmpID];
		if (!tmpAccounts) { return `<div class="cl-accounts-note cl-note"><i class="fas fa-spinner fa-spin"></i> Loading accounts…</div>`; }
		if (!tmpAccounts.length)
		{
			return `<div class="cl-accounts-note cl-note">No configuration admin for this customer yet — <a href="#/GrantAccess/${tmpID}">grant one in Grants</a> first.</div>`;
		}
		const tmpHead = '<div class="cl-accounts-head">Sign in as a configuration admin</div>';
		return tmpHead + tmpAccounts.map((pAccount) =>
			`<button type="button" class="cl-account" onclick="_Pict.providers.CustomersList.impersonateAccount(${Number(pAccount.IDUser)})">`
			+ `<span class="cl-account-avatar"><i class="fas fa-user"></i></span>`
			+ `<span class="cl-account-id"><span class="cl-account-name">${this._esc(pAccount.Name)}</span><span class="cl-account-login">${this._esc(pAccount.LoginID || pAccount.Email || `#${pAccount.IDUser}`)}</span></span>`
			+ `<span class="cl-account-go">Sign in <i class="fas fa-arrow-right-to-bracket"></i></span>`
			+ `</button>`).join('');
	}

	/** Sign in as a specific account (no modal) via the app's per-user impersonation seam. */
	impersonateAccount(pIDUser)
	{
		if (this.app && (typeof this.app.impersonateUser === 'function')) { this.app.impersonateUser(Number(pIDUser)); }
	}

	// ── New Customers tab ────────────────────────────────────────────────────────────────────────────────────

	/** Load the provisioning-request Documents (type HeadLight-NewCustomer). The session is HeadLight (customer 1). */
	async loadNewCustomersAsync()
	{
		this.state.NewLoading = true;
		this.state.NewError = '';
		this.render();
		try
		{
			const tmpRows = await this._getJSON(`Documents/FilteredTo/FBV~DocumentType~EQ~${NEW_CUSTOMER_DOCTYPE}~FBV~Deleted~EQ~0~FSF~Name~ASC~0/0/2000`);
			this.state.NewDocs = (Array.isArray(tmpRows) ? tmpRows : []).map((pDoc) => (
			{
				IDDocument: pDoc.IDDocument,
				GUIDDocument: pDoc.GUIDDocument || '',
				Name: pDoc.Name || `Request #${pDoc.IDDocument}`,
				Status: pDoc.Status || 'Draft',
				StateName: pDoc.StateName || '',
				DocumentDate: pDoc.DocumentDate || '',
				Backfill: String(pDoc.GUIDDocument || '').indexOf(BACKFILL_GUID_PREFIX) === 0,
			})).sort((pA, pB) => String(pA.Name).localeCompare(String(pB.Name)));
			this.state.NewLoaded = true;
		}
		catch (pError)
		{
			this.state.NewError = `Could not load new-customer requests: ${(pError && pError.message) ? pError.message : pError}`;
		}
		this.state.NewLoading = false;
		this.render();
	}

	/** A request is "Provisioned" when its customer exists — a back-fill (existing customer) or an Approved request. */
	_newStatus(pDoc)
	{
		const tmpState = String(pDoc.StateName || pDoc.Status || 'Draft');
		if (pDoc.Backfill || (tmpState === 'Approved')) { return { Label: 'Provisioned', Class: 'prov' }; }
		if (tmpState === 'Rejected') { return { Label: 'Rejected', Class: 'rej' }; }
		if ((tmpState === 'Submitted') || (tmpState === 'Pending')) { return { Label: 'Pending approval', Class: 'pend' }; }
		return { Label: 'Draft', Class: 'draft' };
	}

	_newBodyHTML()
	{
		if (this.state.NewLoading && !this.state.NewLoaded) { return '<div class="cl-note"><i class="fas fa-spinner fa-spin"></i> Loading new-customer requests…</div>'; }
		if (this.state.NewError) { return `<div class="cl-error"><i class="fas fa-triangle-exclamation"></i> ${this._esc(this.state.NewError)}</div>`; }
		const tmpTotal = this.state.NewDocs.length;
		const tmpProvisioned = this.state.NewDocs.filter((pDoc) => this._newStatus(pDoc).Class === 'prov').length;
		const tmpPending = tmpTotal - tmpProvisioned;
		const tmpSeg = (pKey, pLabel, pCount) =>
			`<button type="button" class="cl-seg${this.state.NewStatusFilter === pKey ? ' is-active' : ''}" onclick="_Pict.providers.CustomersList.onNewStatusFilter('${pKey}')">${pLabel} <span class="cl-seg-count">${pCount}</span></button>`;
		return `<div class="cl-newbar">`
			+ `<p class="cl-newsummary"><strong>${tmpTotal}</strong> new-customer request${tmpTotal === 1 ? '' : 's'} · ${tmpProvisioned} provisioned · ${tmpPending} in progress</p>`
			+ `<button type="button" class="cl-newbtn" onclick="_Pict.providers.CustomersList.startNewCustomer()"><i class="fas fa-plus"></i> New Customer</button>`
			+ `</div>`
			+ `<div class="cl-filterrow">`
			+ `<input id="CL-NewFilter" class="cl-filter cl-filter-inrow" type="text" autocomplete="off" placeholder="Filter requests by name…" value="${this._esc(this.state.NewFilter)}" oninput="_Pict.providers.CustomersList.onNewFilter(this.value)" />`
			+ `<div class="cl-segctl">${tmpSeg('all', 'All', tmpTotal)}${tmpSeg('provisioned', 'Provisioned', tmpProvisioned)}${tmpSeg('unprovisioned', 'Not provisioned', tmpPending)}</div>`
			+ `</div>`
			+ `<div id="CL-NewList" class="cl-list">${this._newListHTML()}</div>`;
	}

	_newListHTML()
	{
		const tmpTerm = String(this.state.NewFilter || '').trim().toLowerCase();
		const tmpStatusFilter = this.state.NewStatusFilter;
		const tmpRows = this.state.NewDocs.filter((pDoc) =>
		{
			if (tmpTerm && (String(pDoc.Name).toLowerCase().indexOf(tmpTerm) === -1)) { return false; }
			const tmpIsProv = (this._newStatus(pDoc).Class === 'prov');
			if ((tmpStatusFilter === 'provisioned') && !tmpIsProv) { return false; }
			if ((tmpStatusFilter === 'unprovisioned') && tmpIsProv) { return false; }
			return true;
		});
		if (!tmpRows.length) { return `<div class="cl-note">${(tmpTerm || (tmpStatusFilter !== 'all')) ? 'No requests match your filter.' : 'No new-customer requests yet.'}</div>`; }
		const tmpHeader = '<div class="cl-head-row"><span class="cl-col-name">Customer</span><span class="cl-col-status">Status</span><span class="cl-col-newactions"></span></div>';
		return tmpHeader + tmpRows.map((pDoc) =>
		{
			const tmpStatus = this._newStatus(pDoc);
			return `<div class="cl-item">`
				+ `<div class="cl-row cl-row-static">`
				+ `<span class="cl-col-name">${this._esc(pDoc.Name)}</span>`
				+ `<span class="cl-col-status"><span class="cl-badge cl-badge-${tmpStatus.Class}">${tmpStatus.Label}</span></span>`
				+ `<span class="cl-col-newactions">${this._newRowActions(pDoc, tmpStatus)}</span>`
				+ `</div>`
				+ `</div>`;
		}).join('');
	}

	/** Approval actions for an in-progress request: Draft → Submit; Submitted/Pending → Approve/Reject. */
	_newRowActions(pDoc, pStatus)
	{
		const tmpID = Number(pDoc.IDDocument);
		if (Number(this.state.NewBusyID) === tmpID) { return '<span class="cl-actbusy"><i class="fas fa-spinner fa-spin"></i></span>'; }
		if (pStatus.Class === 'draft')
		{
			return `<button type="button" class="cl-actbtn" onclick="_Pict.providers.CustomersList.transitionRequest(${tmpID},'Submit')"><i class="fas fa-paper-plane"></i> Submit</button>`;
		}
		if (pStatus.Class === 'pend')
		{
			return `<button type="button" class="cl-actbtn cl-actbtn-approve" onclick="_Pict.providers.CustomersList.transitionRequest(${tmpID},'Approve')"><i class="fas fa-check"></i> Approve</button>`
				+ `<button type="button" class="cl-actbtn cl-actbtn-reject" onclick="_Pict.providers.CustomersList.transitionRequest(${tmpID},'Reject')"><i class="fas fa-xmark"></i> Reject</button>`;
		}
		return '';
	}

	/**
	 * Drive an approval transition on a request Document (POST /1.0/DocumentApproval). Approve fires the document's
	 * OnEnteringState.Approved comprehension server-side, provisioning the customer. Reloads the list on success.
	 */
	transitionRequest(pIDDocument, pAction)
	{
		const tmpID = Number(pIDDocument);
		if (!tmpID || Number(this.state.NewBusyID)) { return; }
		const tmpDoc = this.state.NewDocs.find((pDoc) => Number(pDoc.IDDocument) === tmpID);
		const tmpName = tmpDoc ? tmpDoc.Name : `request #${tmpID}`;
		const fGo = async () =>
		{
			this.state.NewBusyID = tmpID;
			this._renderNewList();
			try
			{
				const tmpRes = await this._postJSON('DocumentApproval', { IDDocument: tmpID, Action: pAction });
				if (tmpRes && tmpRes.ErrorCode) { throw new Error(tmpRes.Error || 'the server rejected the transition'); }
				this.state.NewBusyID = 0;
				this.state.NewLoaded = false;
				await this.loadNewCustomersAsync();
				const tmpVerb = (pAction === 'Approve') ? 'Approved and provisioned' : (pAction === 'Reject') ? 'Rejected' : 'Submitted';
				this._toast(`${tmpVerb} "${tmpName}".`, 'success');
			}
			catch (pError)
			{
				this.state.NewBusyID = 0;
				this._toast(`Could not ${pAction.toLowerCase()} "${tmpName}": ${(pError && pError.message) ? pError.message : pError}`, 'error');
				this._renderNewList();
			}
		};
		// Approve (provisions) and Reject get a confirm; Submit is low-stakes.
		if (((pAction === 'Approve') || (pAction === 'Reject')) && this.modal && (typeof this.modal.confirm === 'function'))
		{
			const tmpMsg = (pAction === 'Approve')
				? `Approve "${tmpName}"? This provisions the customer, its admin and integration users, and roles.`
				: `Reject "${tmpName}"? The request stays in the list as Rejected.`;
			this.modal.confirm(tmpMsg, { title: `${pAction} request`, confirmLabel: pAction, cancelLabel: 'Cancel', dangerous: (pAction === 'Reject') }).then((pOk) => { if (pOk) { fGo(); } });
		}
		else { fGo(); }
	}

	onNewFilter(pValue) { this.state.NewFilter = pValue; this._renderNewList(); }

	/** Filter by provisioned status — re-render the body so the segmented control's active pill + the list both update. */
	onNewStatusFilter(pKey) { this.state.NewStatusFilter = ([ 'provisioned', 'unprovisioned' ].indexOf(pKey) > -1) ? pKey : 'all'; this.render(); }

	_renderNewList()
	{
		const tmpCA = this.pict.ContentAssignment;
		const tmpEl = tmpCA.getElement('#CL-NewList');
		if (!tmpEl || !tmpEl.length) { return; }
		const tmpTop = tmpEl[0].scrollTop;
		tmpCA.assignContent('#CL-NewList', this._newListHTML());
		const tmpEl2 = tmpCA.getElement('#CL-NewList');
		if (tmpEl2 && tmpEl2.length) { tmpEl2[0].scrollTop = tmpTop; }
	}

	/** Start a new customer request — the provisioning wizard (which, once wired, saves a Draft request document). */
	startNewCustomer()
	{
		if ((typeof window !== 'undefined') && window.location) { window.location.hash = '#/CustomerProvisioning'; }
	}
}

CustomersListProvider.default_configuration = { ProviderIdentifier: 'CustomersList' };

module.exports = CustomersListProvider;
module.exports.default_configuration = CustomersListProvider.default_configuration;
